import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/dashboard/Sidebar';
import { Award, Download, Calendar, CheckCircle } from 'lucide-react';
import axios from 'axios';

const DashboardCertificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  const STUDENT_ID = 'student-demo-1';
  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/students/${STUDENT_ID}/certificates`);
      setCertificates(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D47A1]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">My Certificates</h1>
          <p className="text-gray-600 mt-1">View and download your earned certificates</p>
        </div>

        <div className="p-6 max-w-7xl mx-auto">
          {certificates.length === 0 ? (
            /* Empty State */
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-12 h-12 text-[#0D47A1]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">No Certificates Yet</h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Complete your courses to earn certificates and showcase your achievements!
              </p>
              <button
                onClick={() => window.location.href = '/dashboard/courses'}
                className="bg-gradient-to-r from-[#0D47A1] to-[#00BCD4] text-white font-bold px-8 py-3 rounded-xl hover:shadow-lg transition-all"
              >
                View My Courses
              </button>
            </div>
          ) : (
            /* Certificates Grid */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certificates.map((cert) => (
                <div
                  key={cert.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-xl transition-all group"
                >
                  {/* Certificate Preview */}
                  <div className="relative bg-gradient-to-br from-[#0D47A1] via-[#1565C0] to-[#00BCD4] p-8 h-48 flex items-center justify-center">
                    <div className="text-center text-white">
                      <Award className="w-16 h-16 mx-auto mb-3 opacity-90" />
                      <div className="text-sm font-semibold opacity-90">Certificate of Completion</div>
                    </div>
                    <div className="absolute top-4 right-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                  </div>

                  {/* Certificate Details */}
                  <div className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2 text-lg">{cert.course_title}</h3>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                      <Calendar className="w-4 h-4" />
                      <span>Issued: {new Date(cert.issued_date).toLocaleDateString()}</span>
                    </div>

                    <button className="w-full bg-[#0D47A1] text-white font-semibold py-3 rounded-xl hover:bg-[#00BCD4] transition-all flex items-center justify-center gap-2 group-hover:shadow-lg">
                      <Download className="w-5 h-5" />
                      Download Certificate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info Card */}
          <div className="mt-8 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
              <Award className="w-5 h-5 text-[#0D47A1]" />
              About Your Certificates
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              CareerLift certificates are industry-recognized credentials that validate your skills and knowledge. 
              Share them on LinkedIn, add them to your resume, and showcase your achievements to potential employers.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardCertificates;